/**
 * 変更管理コンテキスト
 * 変更履歴の管理、差分検出、履歴パネルの状態を一元管理
 */

import React, { createContext, useContext, useCallback, useState, useMemo, useEffect, ReactNode } from 'react';
import {
  saveChangeToHistory,
  getChangeHistory,
  clearChangeHistory,
  getLastSyncTime,
  saveLastSyncTime,
  getRecentChangeCount,
  getChangeHistoryStats
} from '../services/changeHistory';
import { getDeviceId, getDeviceName, getDeviceInfo } from '../services/deviceIdentifier';
import { getFieldLabel, getNestedFieldLabel, formatValue } from '../constants/fieldLabels';
import { saveDispatchReservation } from '../api/dispatchService';
import { reservationSyncService } from '../services/reservationSyncService';

type ChangeType = 'create' | 'update' | 'delete';

interface FieldChange {
  field: string;
  label: string;
  oldValue: any;
  newValue: any;
  rawOldValue: any;
  rawNewValue: any;
}

interface ChangeRecord {
  id: string;
  timestamp: string;
  entityType: string;
  entityId: string | number;
  changeType: ChangeType;
  changeTypeLabel: string;
  source: string;
  deviceId: string;
  deviceName: string;
  changes: FieldChange[];
  summary: string;
  metadata?: Record<string, any>;
}

interface RecordChangeOptions {
  source?: string;
  entityType: string;
  entityId: string | number;
  changeType?: ChangeType;
  changes?: FieldChange[];
  summary?: string;
  metadata?: Record<string, any>;
}

interface ChangeManagerContextValue {
  // 状態
  history: ChangeRecord[];
  historyPanelOpen: boolean;
  unreadCount: number;
  lastViewedTime: Date | null;

  // アクション
  openHistoryPanel: () => void;
  closeHistoryPanel: () => void;
  toggleHistoryPanel: () => void;
  recordChange: {
    (options: RecordChangeOptions): ChangeRecord | null;
    (entityType: string, entityId: string | number, oldData: Record<string, any> | null, newData: Record<string, any> | null, source?: string): ChangeRecord | null;
  };
  clearHistory: () => void;
  refreshHistory: () => void;
  revertChange: (record: ChangeRecord) => Promise<boolean>;

  // ユーティリティ
  detectChanges: (oldObj: Record<string, any> | null, newObj: Record<string, any> | null, prefix?: string) => FieldChange[];
  getChangeType: (oldObj: any, newObj: any) => ChangeType;
  getChangeTypeLabel: (type: ChangeType) => string;

  // デバイス情報
  deviceId: string;
  deviceName: string;
  deviceInfo: Record<string, any>;
}

interface ChangeManagerProviderProps {
  children: ReactNode;
}

const ChangeManagerContext = createContext<ChangeManagerContextValue | null>(null);

/**
 * 2つのオブジェクトの差分を検出
 * @param oldObj 古いオブジェクト
 * @param newObj 新しいオブジェクト
 * @param prefix フィールドプレフィックス（ネスト用）
 * @returns 差分の配列
 */
function detectChanges(oldObj: Record<string, any> | null, newObj: Record<string, any> | null, prefix: string = ''): FieldChange[] {
  const changes: FieldChange[] = [];

  if (!oldObj && !newObj) return changes;

  const old = oldObj || {};
  const updated = newObj || {};

  // 両方のキーを取得
  const allKeys = new Set([...Object.keys(old), ...Object.keys(updated)]);

  // スキップするフィールド
  const skipFields = new Set([
    '_', 'updated_at', 'updatedAt', 'created_at', 'createdAt',
    'resourceId', // vehicle_id と重複（内部用）
    'resource_id', // vehicle_id と重複（内部用）
    'vehicleId',  // vehicle_id と重複（camelCase）
    'status_name', // status から派生
    'status_code', // status から派生
    'communication_tools_display', // communication_tools から派生
    'booking_date', // 更新時に変更されるべきでないが、検出から除外
    'bookingDate',
    'past_reservation_count', // サーバー計算値
    'resource_id_r', // 内部用リソース配列
  ]);

  for (const key of allKeys) {
    // 内部フィールドやメタデータはスキップ
    if (key.startsWith('_') || skipFields.has(key)) continue;

    const fullKey = prefix ? `${prefix}.${key}` : key;
    const oldVal = old[key];
    const newVal = updated[key];

    // ネストされたオブジェクト（detail等）を再帰的に処理
    if (
      key === 'detail' &&
      typeof oldVal === 'object' &&
      typeof newVal === 'object' &&
      oldVal !== null &&
      newVal !== null
    ) {
      const nestedChanges = detectChanges(oldVal, newVal, 'detail');
      changes.push(...nestedChanges);
      continue;
    }

    // 空値（null, undefined, ''）同士の変更はスキップ
    const isOldEmpty = oldVal === null || oldVal === undefined || oldVal === '';
    const isNewEmpty = newVal === null || newVal === undefined || newVal === '';
    if (isOldEmpty && isNewEmpty) continue;

    // 日付フィールドはタイムスタンプで比較（タイムゾーン表記の違いを吸収）
    const isDateField = key.includes('date') || key.includes('Date') ||
                        key === 'start' || key === 'end' ||
                        key === 'created_at' || key === 'updated_at';
    if (isDateField && typeof oldVal === 'string' && typeof newVal === 'string') {
      try {
        const oldTime = new Date(oldVal).getTime();
        const newTime = new Date(newVal).getTime();
        // 1秒以内の差は同じとみなす
        if (!isNaN(oldTime) && !isNaN(newTime) && Math.abs(oldTime - newTime) < 1000) {
          continue;
        }
      } catch {
        // パースに失敗した場合は通常の比較に fallback
      }
    }

    // 値の比較（JSON文字列化して比較）
    const oldStr = JSON.stringify(oldVal) || '';
    const newStr = JSON.stringify(newVal) || '';

    if (oldStr !== newStr) {
      changes.push({
        field: fullKey,
        label: prefix ? getNestedFieldLabel(prefix, key) : getFieldLabel(key),
        oldValue: formatValue(key, oldVal),
        newValue: formatValue(key, newVal),
        rawOldValue: oldVal,
        rawNewValue: newVal,
      });
    }
  }

  return changes;
}

/**
 * 変更タイプを判定
 * @param oldObj 古いオブジェクト（null なら新規）
 * @param newObj 新しいオブジェクト（null なら削除）
 * @returns 変更タイプ
 */
function getChangeType(oldObj: any, newObj: any): ChangeType {
  if (!oldObj && newObj) return 'create';
  if (oldObj && !newObj) return 'delete';
  return 'update';
}

/**
 * 変更タイプのラベルを取得
 * @param type 変更タイプ
 * @returns 日本語ラベル
 */
function getChangeTypeLabel(type: ChangeType): string {
  switch (type) {
    case 'create': return '新規作成';
    case 'update': return '更新';
    case 'delete': return '削除';
    default: return type;
  }
}

export function ChangeManagerProvider({ children }: ChangeManagerProviderProps) {
  const [historyPanelOpen, setHistoryPanelOpen] = useState<boolean>(false);
  const [history, setHistory] = useState<ChangeRecord[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [lastViewedTime, setLastViewedTime] = useState<Date | null>(null);

  // 初期ロード
  useEffect(() => {
    const loadedHistory = getChangeHistory();
    setHistory(loadedHistory);

    // 最後に閲覧した時刻を取得
    const lastViewed = localStorage.getItem('rcal_history_last_viewed');
    if (lastViewed) {
      setLastViewedTime(new Date(lastViewed));
    }

    // 未読数を計算
    updateUnreadCount(loadedHistory, lastViewed ? new Date(lastViewed) : null);
  }, []);

  // Pusherから変更が記録されたときにUIを更新
  useEffect(() => {
    const handlePusherChange = (e: Event) => {
      const record = (e as CustomEvent).detail;
      if (record) {
        setHistory(prev => [record, ...prev].slice(0, 500));
        // pusher からの変更は未読にカウント
        setUnreadCount(prev => prev + 1);
      }
    };
    window.addEventListener('pusher-change-recorded', handlePusherChange);
    return () => window.removeEventListener('pusher-change-recorded', handlePusherChange);
  }, []);

  /**
   * 未読数を更新
   */
  const updateUnreadCount = useCallback((historyData: ChangeRecord[], lastViewed: Date | null) => {
    if (!lastViewed) {
      // 一度も見ていない場合は全て未読
      setUnreadCount(historyData.length);
      return;
    }

    const unread = historyData.filter(record => {
      const recordTime = new Date(record.timestamp);
      return recordTime > lastViewed;
    }).length;

    setUnreadCount(unread);
  }, []);

  /**
   * 履歴パネルを開く
   */
  const openHistoryPanel = useCallback(() => {
    setHistoryPanelOpen(true);

    // 閲覧時刻を更新
    const now = new Date();
    setLastViewedTime(now);
    localStorage.setItem('rcal_history_last_viewed', now.toISOString());
    setUnreadCount(0);
  }, []);

  /**
   * 履歴パネルを閉じる
   */
  const closeHistoryPanel = useCallback(() => {
    setHistoryPanelOpen(false);
  }, []);

  /**
   * 履歴パネルをトグル
   */
  const toggleHistoryPanel = useCallback(() => {
    if (historyPanelOpen) {
      closeHistoryPanel();
    } else {
      openHistoryPanel();
    }
  }, [historyPanelOpen, openHistoryPanel, closeHistoryPanel]);

  /**
   * 変更を検出して記録する
   */
  const recordChange = useCallback((entityTypeOrOptions: string | RecordChangeOptions, entityId?: string | number, oldData?: Record<string, any> | null, newData?: Record<string, any> | null, source: string = 'user'): ChangeRecord | null => {
    // Sync用のオブジェクト形式で呼ばれた場合
    if (entityTypeOrOptions && typeof entityTypeOrOptions === 'object') {
      const opts = entityTypeOrOptions as RecordChangeOptions;
      const record: ChangeRecord = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        entityType: opts.entityType,
        entityId: opts.entityId,
        changeType: opts.changeType || 'update',
        changeTypeLabel: getChangeTypeLabel(opts.changeType || 'update'),
        source: opts.source || 'sync',
        deviceId: getDeviceId(),
        deviceName: getDeviceName(),
        changes: opts.changes || [],
        summary: opts.summary || `${opts.entityType} #${opts.entityId} が更新されました`,
        metadata: opts.metadata || {},
      };

      // LocalStorageに保存
      saveChangeToHistory(record);

      // 状態を更新
      setHistory(prev => [record, ...prev].slice(0, 500));

      // sync/pusher からの変更は未読にカウント
      if (opts.source !== 'user') {
        setUnreadCount(prev => prev + 1);
      }

      return record;
    }

    // 従来の位置引数形式
    const entityType = entityTypeOrOptions as string;
    const changes = detectChanges(oldData || null, newData || null);
    const changeType = getChangeType(oldData, newData);

    // 変更がない場合は記録しない（削除は変更がなくても記録）
    if (changes.length === 0 && changeType !== 'delete') {
      return null;
    }

    const record: ChangeRecord = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      entityType,
      entityId: entityId!,
      changeType,
      changeTypeLabel: getChangeTypeLabel(changeType),
      source,
      deviceId: getDeviceId(),
      deviceName: getDeviceName(),
      changes,
      // 表示用のサマリー
      summary: buildChangeSummary(entityType, entityId!, changeType, changes, oldData || null, newData || null),
    };

    // LocalStorageに保存
    saveChangeToHistory(record);

    // 状態を更新
    setHistory(prev => [record, ...prev].slice(0, 500));

    // ユーザー自身の操作は未読にカウントしない
    if (source !== 'user') {
      setUnreadCount(prev => prev + 1);
    }

    return record;
  }, []);

  /**
   * 変更サマリーを作成
   */
  const buildChangeSummary = (entityType: string, entityId: string | number, changeType: ChangeType, changes: FieldChange[], oldData: Record<string, any> | null, newData: Record<string, any> | null): string => {
    const entityLabels: Record<string, string> = {
      reservation: '予約',
      vehicle: '車両',
      resource: 'リソース',
      vehicle_type: '車種',
    };

    const entityLabel = entityLabels[entityType] || entityType;
    const data = newData || oldData;

    // 予約の場合は顧客名を表示
    let identifier = '';
    if (entityType === 'reservation') {
      identifier = data?.customer_name || data?.customerName || `#${entityId}`;
    } else if (entityType === 'vehicle') {
      identifier = data?.vehicle_code || data?.vehicleCode || `#${entityId}`;
    } else {
      identifier = `#${entityId}`;
    }

    const typeLabel = getChangeTypeLabel(changeType);

    if (changeType === 'create') {
      return `${entityLabel}「${identifier}」を${typeLabel}しました`;
    } else if (changeType === 'delete') {
      return `${entityLabel}「${identifier}」を${typeLabel}しました`;
    } else {
      const fieldCount = changes.length;
      return `${entityLabel}「${identifier}」の${fieldCount}項目を${typeLabel}しました`;
    }
  };

  /**
   * 履歴をクリア
   */
  const clearHistory = useCallback(() => {
    clearChangeHistory();
    setHistory([]);
    setUnreadCount(0);
  }, []);

  /**
   * 履歴を再読み込み
   */
  const refreshHistory = useCallback(() => {
    const loaded = getChangeHistory();
    setHistory(loaded);
    updateUnreadCount(loaded, lastViewedTime);
  }, [lastViewedTime, updateUnreadCount]);

  /**
   * 変更を復元する（予約のみ対応）
   */
  const revertChange = useCallback(async (record: ChangeRecord): Promise<boolean> => {
    if (record.entityType !== 'reservation') {
      throw new Error('予約以外の復元は未対応です');
    }
    if (!record.changes || record.changes.length === 0) {
      throw new Error('復元する変更がありません');
    }

    // 1. 現在の予約データをキャッシュから取得
    const currentData = reservationSyncService.getReservation(String(record.entityId));
    if (!currentData) {
      throw new Error('予約データが見つかりません（キャッシュに未ロード）');
    }

    // 2. rawOldValue から復元パッチを構築
    const patch: Record<string, any> = {};
    const detailPatch: Record<string, any> = {};
    for (const change of record.changes) {
      if (change.field.startsWith('detail.')) {
        detailPatch[change.field.replace('detail.', '')] = change.rawOldValue;
      } else {
        patch[change.field] = change.rawOldValue;
      }
    }

    // 3. 現在データにパッチを適用
    const restored: any = { ...currentData, ...patch };
    if (Object.keys(detailPatch).length > 0) {
      restored.detail = { ...((currentData as any).detail || {}), ...detailPatch };
    }

    // 4. API保存
    const saved = await saveDispatchReservation(restored);
    if (!saved) {
      throw new Error('予約の保存に失敗しました');
    }

    // 5. キャッシュを更新
    reservationSyncService.cachedReservations.set(String(saved.id), saved);

    // 6. 復元を履歴に記録
    const revertedFields = record.changes.map(c => c.label || c.field).join(', ');
    recordChange({
      source: 'user',
      entityType: 'reservation',
      entityId: record.entityId,
      changeType: 'update',
      summary: `予約「${(currentData as any).customer_name || record.entityId}」の復元: ${revertedFields}`,
      changes: record.changes.map(c => ({
        ...c,
        oldValue: c.newValue,
        newValue: c.oldValue,
        rawOldValue: c.rawNewValue,
        rawNewValue: c.rawOldValue,
      })),
    });

    return true;
  }, [recordChange]);

  const value = useMemo((): ChangeManagerContextValue => ({
    // 状態
    history,
    historyPanelOpen,
    unreadCount,
    lastViewedTime,

    // アクション
    openHistoryPanel,
    closeHistoryPanel,
    toggleHistoryPanel,
    recordChange: recordChange as ChangeManagerContextValue['recordChange'],
    clearHistory,
    refreshHistory,
    revertChange,

    // ユーティリティ
    detectChanges,
    getChangeType,
    getChangeTypeLabel,

    // デバイス情報
    deviceId: getDeviceId(),
    deviceName: getDeviceName(),
    deviceInfo: getDeviceInfo(),
  }), [
    history,
    historyPanelOpen,
    unreadCount,
    lastViewedTime,
    openHistoryPanel,
    closeHistoryPanel,
    toggleHistoryPanel,
    recordChange,
    clearHistory,
    refreshHistory,
    revertChange,
  ]);

  return (
    <ChangeManagerContext.Provider value={value}>
      {children}
    </ChangeManagerContext.Provider>
  );
}

/**
 * 変更管理コンテキストを使用するフック
 * @returns コンテキスト値
 */
export function useChangeManager(): ChangeManagerContextValue {
  const context = useContext(ChangeManagerContext);
  if (!context) {
    throw new Error('useChangeManager must be used within a ChangeManagerProvider');
  }
  return context;
}

export default ChangeManagerContext;
